import type { ReactNode } from 'react';
import { cx } from '../../lib/cx.ts';
import type { Tone } from './Badge.tsx';
import {
  CurrencyIcon,
  FileIcon,
  FlagIcon,
  MailIcon,
  MessageIcon,
  VideoIcon,
} from '../icons.tsx';

/**
 * The spine of the lead detail page. Calls, touches, stage changes and
 * payments interleave in one chronological column, because the question the
 * page answers is "what happened with this person, in order" and four
 * separate lists cannot answer it.
 */
export type TimelineKind = 'call' | 'touch' | 'email' | 'stage' | 'deal' | 'payment';

const KIND: Record<TimelineKind, { icon: ReactNode; tone: Tone }> = {
  call: { icon: <VideoIcon size={13} />, tone: 'open' },
  touch: { icon: <MessageIcon size={13} />, tone: 'neutral' },
  email: { icon: <MailIcon size={13} />, tone: 'neutral' },
  stage: { icon: <FlagIcon size={13} />, tone: 'accent' },
  deal: { icon: <FileIcon size={13} />, tone: 'accent' },
  payment: { icon: <CurrencyIcon size={13} />, tone: 'won' },
};

const NODE_TONE: Record<Tone, string> = {
  won: 'text-won',
  lost: 'text-lost',
  risk: 'text-risk',
  stalled: 'text-stalled',
  open: 'text-open',
  neutral: 'text-fg-subtle',
  accent: 'text-accent-text',
};

export function Timeline({ children }: { children: ReactNode }) {
  return <ol className="relative">{children}</ol>;
}

export interface TimelineItemProps {
  kind: TimelineKind;
  title: ReactNode;
  /** Right-aligned timestamp. Keep it short: `Mar 4` or `Mar 4, 2:15 PM`. */
  timestamp?: ReactNode;
  /** Secondary line under the title: duration, channel, amount. */
  meta?: ReactNode;
  /** Optional expanded content, e.g. an analysis summary or a message body. */
  children?: ReactNode;
  /** Overrides the dot colour, e.g. a `closed_lost` stage event. */
  tone?: Tone;
  onClick?: () => void;
}

export function TimelineItem({
  kind,
  title,
  timestamp,
  meta,
  children,
  tone,
  onClick,
}: TimelineItemProps) {
  const config = KIND[kind];
  const nodeTone = NODE_TONE[tone ?? config.tone];

  return (
    <li className="group relative flex gap-3 pb-5 last:pb-0">
      {/* The spine, drawn per item and hidden on the last so the line stops
          at the final node instead of trailing into whitespace. */}
      <span
        aria-hidden="true"
        className="absolute top-7 bottom-0 left-3.5 w-px bg-border group-last:hidden"
      />

      <span
        className={cx(
          'relative z-10 flex size-7 shrink-0 items-center justify-center rounded-full border border-border bg-surface-raised',
          nodeTone,
        )}
      >
        {config.icon}
      </span>

      <div
        className={cx(
          'min-w-0 flex-1 rounded-md px-2.5 py-1 transition',
          onClick && 'cursor-pointer hover:bg-surface',
        )}
        onClick={onClick}
      >
        <div className="flex items-baseline justify-between gap-3">
          <p className="min-w-0 truncate text-sm font-medium text-fg">{title}</p>
          {timestamp && (
            <span className="numeric shrink-0 text-xs text-fg-subtle">{timestamp}</span>
          )}
        </div>
        {meta && <p className="mt-0.5 text-xs text-fg-subtle">{meta}</p>}
        {children && <div className="mt-2">{children}</div>}
      </div>
    </li>
  );
}

/** Date break for long histories, so months stay findable when scrolling. */
export function TimelineDivider({ label }: { label: string }) {
  return (
    <li className="relative flex items-center gap-3 pb-5">
      <span className="flex size-7 shrink-0 items-center justify-center">
        <span className="size-1.5 rounded-full bg-border-strong" />
      </span>
      <span className="text-2xs font-medium tracking-tight text-fg-faint">{label}</span>
    </li>
  );
}
